import { useCallback, useEffect, useState } from 'react'

import { Plus } from '@gravity-ui/icons'
import { Button, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ConfirmCloneDialog } from '@/components/ui/ConfirmCloneDialog'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { useReportsStore } from '@/store/reports.store'

import { ReconciliationDialog } from './components/ReconciliationDialog'
import { ReportsFilters } from './components/ReportsFilters'
import { ReportsTable } from './components/ReportsTable'
import { UploadPaidRoadsDialog } from './components/UploadPaidRoadsDialog'

export default function ReportsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    reports,
    fetchReports,
    isLoading,
    filters,
    setFilters,
    deleteReport,
    duplicateReport,
  } = useReportsStore()


  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const handleDateRangeChange = useCallback((from: string, to: string) => {
    setFilters({ from, to })
  }, [setFilters])


  const onView = useCallback((id: string) => {
    navigate(`/reports/${id}`)
  }, [navigate])

  const onEdit = useCallback((id: string) => {
    navigate(`/reports/${id}/edit`)
  }, [navigate])

  const handleNewReportClick = useCallback(() => {
    navigate('/reports/new')
  }, [navigate])

  const handleDriverChange = useCallback((id: string) => {
    setFilters({ driverID: id })
  }, [setFilters])

  const handleVehicleChange = useCallback((id: string) => {
    setFilters({ vehicleID: id })
  }, [setFilters])

  const handleDocumentChange = useCallback((doc: string) => {
    setFilters({ document: doc })
  }, [setFilters])

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | undefined>()

  const handleDeleteTrigger = useCallback((id: string) => {
    setDeletingId(id)
    setIsDeleteOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (deletingId) {
      await deleteReport(deletingId)
      setIsDeleteOpen(false)
      setDeletingId(undefined)
    }
  }, [deletingId, deleteReport])

  const [isCloneOpen, setIsCloneOpen] = useState(false)
  const [cloningId, setCloningId] = useState<string | undefined>()
  const [isCloning, setIsCloning] = useState(false)

  const handleCloneTrigger = useCallback((id: string) => {
    setCloningId(id)
    setIsCloneOpen(true)
  }, [])

  const handleCloneConfirm = useCallback(async () => {
    if (!cloningId) return
    setIsCloning(true)
    try {
      const newReportId = await duplicateReport(cloningId)
      setIsCloneOpen(false)
      setCloningId(undefined)
      navigate(`/reports/${newReportId}/edit`)
    } catch (error) {
      console.error('Failed to clone report:', error)
    } finally {
      setIsCloning(false)
    }
  }, [cloningId, duplicateReport, navigate])

  const [isReconciliationOpen, setIsReconciliationOpen] = useState(false)
  const [isUploadPaidRoadsOpen, setIsUploadPaidRoadsOpen] = useState(false)

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('reports.title')}
        subtitle={t('reports.subtitle')}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsReconciliationOpen(true)}
            >
              {t('reports.reconciliation')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsUploadPaidRoadsOpen(true)}
            >
              {t('reports.upload_paid_roads')}
            </Button>
            <Button
              onClick={handleNewReportClick}
            >
              <Plus  />
              {t('reports.new_report')}
            </Button>
          </>
        }
      />

      <ReportsFilters
        dateFrom={filters.from}
        dateTo={filters.to}
        onDateRangeChange={handleDateRangeChange}
        driverID={filters.driverID}
        onDriverChange={handleDriverChange}
        vehicleID={filters.vehicleID}
        onVehicleChange={handleVehicleChange}
        document={filters.document}
        onDocumentChange={handleDocumentChange}
      />

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <ReportsTable
          reports={reports}
          onView={onView}
          onEdit={onEdit}
          onClone={handleCloneTrigger}
          onDelete={handleDeleteTrigger}
        />

      )}

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('reports.delete_confirm', 'Вы уверены, что хотите удалить этот отчёт? Это действие нельзя будет отменить.')}
        isLoading={isLoading}
      />

      <ConfirmCloneDialog
        isOpen={isCloneOpen}
        onOpenChange={setIsCloneOpen}
        onConfirm={handleCloneConfirm}
        title={t('reports.clone_report')}
        description={t('reports.clone_confirm', 'Это действие создаст копию этого отчёта и откроет её для редактирования.')}
        isLoading={isCloning}
      />

      <ReconciliationDialog
        isOpen={isReconciliationOpen}
        onOpenChange={setIsReconciliationOpen}
      />

      <UploadPaidRoadsDialog
        isOpen={isUploadPaidRoadsOpen}
        onOpenChange={setIsUploadPaidRoadsOpen}
        onSuccess={fetchReports}
      />
    </div>
  )
}
